import { useKioskEvent } from "@/app/directories/kiosk/contexts/KioskEventContext";
import type { HrtOption } from "@/app/directories/shop/[clientSlug]/products/[productId]/components/ProductContext";
import {
  getTranslatedProductDescription,
  getTranslatedProductKioskDescription,
  getTranslatedProductKioskTitle,
  getTranslatedProductName,
} from "@/app/directories/shop/helper";
import { formatAsDollars } from "@/app/directories/shop/lib/formatAsDollars";
import { ProductType } from "@/lib/graphql/generated-gql";
import { getAllEnabledOrgConfigFlagsByOrgId } from "@frontend/lib/actions/actions";
import { getOrganizationAssetsForOrg } from "@frontend/lib/actions/config-actions";
import { EVENT_CONTEXTS } from "@frontend/lib/analytics";
import { trackEvent } from "@frontend/lib/analytics";
import { EVENT_CATEGORIES } from "@frontend/lib/analytics";
import type { GetProductsByCategoryIdQuery } from "@frontend/lib/graphql/generated-gql";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from "@frontend/shared/components/ui/drawer";
import { cn } from "@frontend/shared/lib/utils";
import { sendGTMEvent } from "@next/third-parties/google";
import { useIsFetching, useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { useLocale } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import type React from "react";
import { EligibilityButton } from "../eligibility/EligibilityButton";
import { PriceToggleKiosk } from "../priceToggle/priceToggleKiosk";
import { usePriceMap } from "../priceToggle/usePriceMap";

export const FeaturedCarouselSlideKiosk = ({
  product,
  clientSlug,
  orgId,
  isMinimal = false,
  onBuyNowClick,
  primaryActionText,
  kioskMode = false,
}: {
  product: GetProductsByCategoryIdQuery["findManyProducts"][number];
  clientSlug: string;
  orgId: number;
  isMinimal?: boolean;
  onBuyNowClick?: () => void;
  primaryActionText?: string;
  kioskMode?: boolean;
}) => {
  const locale = useLocale();
  const languageCode = locale.replace("-", "_");
  const productTranslation = product.ProductTranslation?.find(
    (t) => t.languageCode === languageCode,
  );

  const t = useTranslations("Shop");
  const primaryActionTextTranslated = primaryActionText || t("BuyNow");
  return (
    <div
      className="keen-slider__slide flex relative justify-between"
      data-component="FeaturedCarouselSlideKiosk"
      data-carousel-type="featured"
      data-product-id={product.id}
      data-product-name={product.name}
    >
      <Image
        className="rounded-2xl object-cover w-[286px] h-[325px]"
        src={product.displayMetadata?.imageUrl ?? ""}
        width={286}
        height={325}
        alt="Product Image"
        aria-label="Product"
      />
      <FeaturedCarouselSlideContent
        product={product}
        productTranslation={productTranslation}
        clientSlug={clientSlug}
        isMinimal={isMinimal}
        orgId={orgId}
        onBuyNowClick={onBuyNowClick}
        primaryActionText={primaryActionTextTranslated}
        kioskMode={kioskMode}
      />
    </div>
  );
};

export const FeaturedCarouselSlideContentMobile = ({
  product,
  productTranslation,
  clientSlug,
  isMinimal,
  orgId,
  onBuyNowClick,
  primaryActionText,
}) => {
  const locale = useLocale();
  const languageCode = locale.replace("-", "_");
  const pricingStatus = useBoundStore.use.pricingStatus();
  const isFetchingPrice = !!useIsFetching({ queryKey: ["updatePrices"] });
  const pricesLoaded = pricingStatus === "SUCCESS" && !isFetchingPrice;
  const t = useTranslations("Shop");

  const { data: configFlags } = useQuery({
    queryKey: ["configFlags", orgId],
    queryFn: () => getAllEnabledOrgConfigFlagsByOrgId(orgId),
    enabled: !!orgId,
  });

  const { data: organizationAssets } = useQuery({
    queryKey: ["organizationAssets", orgId],
    queryFn: () => getOrganizationAssetsForOrg(orgId),
    enabled: !!orgId,
  });

  const primaryColor = organizationAssets?.primaryColor ?? "#000000";

  const {
    isInsuredMode,
    handlePriceModeChange,
    lowestPrice,
    isToggleDisabled,
    lowestComparisonPrice,
    isSelfPayOnly,
  } = usePriceMap(product.productToHrt as unknown as HrtOption[]);

  const handlePriceModeChangeKiosk = (checked: boolean) => {
    handlePriceModeChange(checked);
  };

  const handleBuyNowClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (
      (product.product_type === ProductType.PdpAndBuyNow ||
        product.product_type === ProductType.BuyNow) &&
      onBuyNowClick
    ) {
      onBuyNowClick();

      sendGTMEvent({
        event: "featured_product_clicked",
        product_name: product.name,
      });
      trackEvent({
        category: EVENT_CATEGORIES.TRANSACTION,
        context: EVENT_CONTEXTS.STOREFRONT,
        action: "featured_product_clicked",
        metadata: {
          product_name: product.name,
        },
      });
    }
  };

  return (
    <div
      className={cn(
        "flex items-center lg:hidden sm:block absolute bottom-6 w-full",
      )}
    >
      <div className="flex flex-col items-start gap-0">
        <div className="p-3 flex flex-col gap-1 pl-[14px]">
          <h3 className="config-h3 lg:text-[40px] sm:text-[24px] text-white">
            {getTranslatedProductName(product, languageCode)}
          </h3>
          <p className="font-suisse font-light text-sm leading-[18px] w-full text-white">
            {getTranslatedProductDescription(product, languageCode)}
          </p>
        </div>

        <div className="rounded-b-[20px] flex flex-col w-full h-full p-3 mb-[2px] bg-black/10 backdrop-blur-xl gap-3">
          {isMinimal ? (
            <p className="text-xs font-light">
              {product.productToHrt.length > 1 && (
                <span>{t("StartingAt")} </span>
              )}
              {product.displayMetadata?.preDiscount && (
                <span className="font-mono line-through mr-1">
                  ${product.displayMetadata?.preDiscount}
                </span>
              )}
              <span className="font-mono">
                {formatAsDollars(lowestPrice, t)}
              </span>

              {!configFlags?.no_insured_procedures && t("WithoutInsurance")}
            </p>
          ) : (
            <PriceToggleKiosk
              checked={isInsuredMode}
              handlePriceModeChangeKiosk={handlePriceModeChangeKiosk}
              price={isInsuredMode ? lowestPrice : lowestComparisonPrice}
              hasMultipleHrts={product.productToHrt.length > 1}
              isSelfPayOnlyClient={!!configFlags?.no_insured_procedures}
              isProductSelfPayOnly={isSelfPayOnly}
              preDiscountprice={product.displayMetadata?.preDiscount}
              pricesLoaded={pricesLoaded}
              hrtIds={product.productToHrt.map((option) => option.hrt.hrtId)}
            />
          )}
          <EligibilityButton
            link={`/${clientSlug}/products/${product.id}`}
            orgId={orgId}
          >
            {product.product_type === ProductType.PdpAndBuyNow ? (
              <div className="flex flex-col gap-2 w-full">
                <button
                  type="button"
                  onClick={handleBuyNowClick}
                  className="rounded-full bg-black text-white p-6 sm:py-3 lg:py-3 w-full flex justify-center"
                >
                  <p className="config-h5 !text-base text-center">
                    {primaryActionText}
                  </p>
                </button>
                <Link
                  href={`/${clientSlug}/products/${product.id}`}
                  className="rounded-full border border-white text-white p-6 sm:py-3 lg:py-3 w-full flex justify-center"
                  onClick={() => {
                    sendGTMEvent({
                      event: "featured_product_clicked",
                      product_name: product.name,
                    });
                    trackEvent({
                      category: EVENT_CATEGORIES.TRANSACTION,
                      context: EVENT_CONTEXTS.STOREFRONT,
                      action: "featured_product_clicked",
                      metadata: {
                        product_name: product.name,
                      },
                    });
                  }}
                >
                  <p className="config-h5 !text-base text-center">
                    {t("ViewDetails")}
                  </p>
                </Link>
              </div>
            ) : product.product_type === ProductType.BuyNow ? (
              <button
                type="button"
                onClick={handleBuyNowClick}
                className="rounded-full text-white p-6 sm:py-3 lg:py-3 w-full flex justify-center"
                style={{ backgroundColor: primaryColor || "#000000" }}
              >
                <p className="config-h5 !text-base text-center">
                  {primaryActionText}
                </p>
              </button>
            ) : (
              <Link
                href={`/${clientSlug}/products/${product.id}`}
                className="rounded-full bg-black text-white p-6 sm:py-3 lg:py-3 w-full flex justify-center"
                onClick={() => {
                  sendGTMEvent({
                    event: "featured_product_clicked",
                    product_name: product.name,
                  });
                  trackEvent({
                    category: EVENT_CATEGORIES.TRANSACTION,
                    context: EVENT_CONTEXTS.STOREFRONT,
                    action: "featured_product_clicked",
                    metadata: {
                      product_name: product.name,
                    },
                  });
                }}
              >
                <p className="config-h5 !text-base text-center">
                  {primaryActionText}
                </p>
              </Link>
            )}
          </EligibilityButton>
        </div>
      </div>
    </div>
  );
};

export const FeaturedCarouselSlideContent = ({
  product,
  productTranslation,
  clientSlug,
  isMinimal,
  orgId,
  onBuyNowClick,
  primaryActionText,
  kioskMode,
}) => {
  const locale = useLocale();
  const languageCode = locale.replace("-", "_");
  const pricingStatus = useBoundStore.use.pricingStatus();
  const isFetchingPrice = !!useIsFetching({ queryKey: ["updatePrices"] });
  const pricesLoaded = pricingStatus === "SUCCESS" && !isFetchingPrice;
  const t = useTranslations("Shop");
  const { pricingMap, isPendingPricesMode, send, getShouldShowPricingLoading } =
    useKioskEvent();

  const { data: configFlags } = useQuery({
    queryKey: ["configFlags", orgId],
    queryFn: () => getAllEnabledOrgConfigFlagsByOrgId(orgId),
    enabled: !!orgId,
  });

  const { data: organizationAssets } = useQuery({
    queryKey: ["organizationAssets", orgId],
    queryFn: () => getOrganizationAssetsForOrg(orgId),
    enabled: !!orgId,
  });

  const primaryColor = organizationAssets?.primaryColor ?? "#000000";

  const {
    isInsuredMode,
    handlePriceModeChange,
    lowestPrice,
    isToggleDisabled,
    lowestComparisonPrice,
    isSelfPayOnly,
  } = usePriceMap(product.productToHrt, {
    pricingMap,
    onPriceModeChange: (newPricingMap) =>
      send({
        type: "SET_PRICING_MAP",
        data: { newPricingMap },
      }),
  });

  const handlePriceModeChangeKiosk = (checked: boolean) => {
    handlePriceModeChange(checked);
  };

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleBuyNowClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (
      (product.product_type === ProductType.PdpAndBuyNow ||
        product.product_type === ProductType.BuyNow) &&
      onBuyNowClick
    ) {
      onBuyNowClick();

      sendGTMEvent({
        event: "featured_product_clicked",
        value: product.name,
      });
      trackEvent({
        category: EVENT_CATEGORIES.TRANSACTION,
        context: EVENT_CONTEXTS.STOREFRONT,
        action: "featured_product_clicked",
        metadata: {
          product_name: product.name,
        },
      });
    }
  };

  const shouldShowLoading = getShouldShowPricingLoading(
    product.productToHrt.map((option) => option.hrt.hrtId),
    lowestPrice,
  );

  return (
    <div style={{ paddingLeft: "32px" }} className={cn("flex w-full")}>
      <div className="flex-1 flex flex-col h-full items-start justify-center lg:gap-5 sm:gap-3">
        <h3 className="kiosk-h1 !text-3xl max-w-[350px]">
          {getTranslatedProductName(product, languageCode)}
        </h3>
        <div className="max-w-[350px]">
          <p className="kiosk-body-lg !text-base !font-light line-clamp-2">
            {getTranslatedProductDescription(product, languageCode)}
          </p>
          {getTranslatedProductDescription(product, languageCode) && (
            <button
              type="button"
              onClick={() => setIsDrawerOpen(true)}
              className="text-sm font-light underline mt-1"
            >
              {t("ReadMore")}
            </button>
          )}
        </div>

        <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
          <DrawerContent>
            <DrawerTitle className="sr-only">
              {getTranslatedProductName(product, languageCode)} Description
            </DrawerTitle>
            <div className="py-6 md:py-12 px-6 md:px-10 min-h-[50vh] max-h-[70vh] overflow-y-auto flex flex-col justify-between">
              <div className="flex flex-col gap-4 w-full">
                <div className="flex justify-end w-full">
                  <X
                    className="w-6 h-6 cursor-pointer"
                    onClick={() => setIsDrawerOpen(false)}
                  />
                </div>
                <h3 className=" mb-4">
                  {getTranslatedProductKioskTitle(product, languageCode)}
                </h3>
                <div className="flex flex-col gap-4">
                  {getTranslatedProductKioskDescription(product, languageCode)}
                </div>
              </div>
              <div className="flex justify-end">
                <motion.button
                  type="button"
                  onClick={() => setIsDrawerOpen(false)}
                  className="rounded-full px-6 py-3 w-fit flex justify-center bg-transparent border hover:!bg-black hover:!text-white hover:!border-black text-sm text-center"
                  style={{
                    color: primaryColor || "#000000",
                    borderColor: primaryColor || "#000000",
                  }}
                  whileHover={{ color: "#ffffff", backgroundColor: "#000000" }}
                >
                  {t("GotIt")}
                </motion.button>
              </div>
            </div>
          </DrawerContent>
        </Drawer>

        <PriceToggleKiosk
          checked={isInsuredMode}
          handlePriceModeChangeKiosk={handlePriceModeChangeKiosk}
          price={lowestPrice}
          hasMultipleHrts={product.productToHrt.length > 1}
          isSelfPayOnlyClient={!!configFlags?.no_insured_procedures}
          isProductSelfPayOnly={isSelfPayOnly}
          preDiscountprice={product.displayMetadata?.preDiscount}
          pricesLoaded={pricesLoaded}
          priceToggleColor={primaryColor}
          hrtIds={product.productToHrt.map((option) => option.hrt.hrtId)}
        />

        <EligibilityButton
          link={`/${clientSlug}/products/${product.id}`}
          orgId={orgId}
          classNames="max-w-[400px]"
        >
          <div className="flex flex-row gap-4">
            <button
              type="button"
              onClick={handleBuyNowClick}
              disabled={shouldShowLoading}
              className="rounded-full text-white p-6 sm:py-3 lg:py-3 w-[140px] flex justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: primaryColor || "#000000" }}
            >
              <p className="config-p2 !text-base text-center">
                {primaryActionText}
              </p>
            </button>
          </div>
        </EligibilityButton>
      </div>
    </div>
  );
};
